/**
 * Centralized API client for consistent fetch behavior
 * Automatically includes credentials and handles common request patterns
 */

// Get base URL from environment variable or fallback to '/api'
const getBaseUrl = () => {
  return process.env.REACT_APP_API_BASE_URL || '/api';
};

/**
 * Centralized fetch wrapper with consistent defaults
 * @param {string} path - API endpoint path (e.g., '/api/analyze-z-penetration')
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
export const apiFetch = async (path, options = {}) => {
  const baseUrl = getBaseUrl();
  const url = path.startsWith('http') || path.startsWith('/api') ? path : `${baseUrl}${path}`;
  
  // Default options
  const defaultOptions = {
    credentials: 'include', // Always send session cookies for auth
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };
  
  // Auto-stringify body if it's a plain object
  if (defaultOptions.body && typeof defaultOptions.body === 'object' && !defaultOptions.body.constructor.name.includes('Form')) {
    defaultOptions.body = JSON.stringify(defaultOptions.body);
  }
  
  return fetch(url, defaultOptions);
};

/**
 * Helper for GET requests
 * @param {string} path - API endpoint path
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiGet = async (path, options = {}) => {
  const response = await apiFetch(path, { method: 'GET', ...options });
  if (!response.ok) {
    throw new Error(`API GET failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Helper for POST requests
 * @param {string} path - API endpoint path
 * @param {any} data - Request body data
 * @param {object} options - Additional fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const apiPost = async (path, data, options = {}) => {
  const response = await apiFetch(path, { 
    method: 'POST', 
    body: data,
    ...options 
  });
  if (!response.ok) {
    throw new Error(`API POST failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export default { apiFetch, apiGet, apiPost };